import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * User information stored in auth context
 */
export interface UserInfo {
  userId?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  tenantId?: string;
  tenantSlug?: string;
  tenantName?: string;
  profilePicture?: string;
}

/**
 * Auth context state and methods
 */
interface AuthContextType {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Auth token (JWT) */
  token: string | null;
  /** User information */
  userInfo: UserInfo | null;
  /** Whether auth state is being loaded */
  isLoading: boolean;
  /** Login function */
  login: (token: string, userInfo: UserInfo) => void;
  /** Logout function */
  logout: () => void;
  /** Update user info (e.g., after profile edit) */
  updateUserInfo: (updates: Partial<UserInfo>) => void;
  /** Refresh auth state from localStorage */
  refreshAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth Provider component - wraps app to provide auth context
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    setToken(null);
    setUserInfo(null);
  }, []);

  const loadAuthFromStorage = useCallback(() => {
    setIsLoading(true);
    try {
      const storedToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const storedUserInfo = localStorage.getItem('userInfo');

      if (storedToken && storedUserInfo) {
        try {
          const parsedUserInfo = JSON.parse(storedUserInfo);
          setToken(storedToken);
          setUserInfo(parsedUserInfo);
        } catch {
          // Invalid stored data, clear it
          clearAuth();
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [clearAuth]);

  /**
   * Load auth state from localStorage on mount
   */
  useEffect(() => {
    loadAuthFromStorage();
  }, [loadAuthFromStorage]);

  /**
   * Login - store token and user info
   */
  const login = useCallback((newToken: string, newUserInfo: UserInfo) => {
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('userInfo', JSON.stringify(newUserInfo));
    setToken(newToken);
    setUserInfo(newUserInfo);
  }, []);

  /**
   * Logout - clear all auth data
   */
  const logout = useCallback(() => {
    clearAuth();
    // Optionally redirect to login
    window.location.href = '/login';
  }, [clearAuth]);

  /**
   * Update user info (partial update)
   */
  const updateUserInfo = useCallback((updates: Partial<UserInfo>) => {
    setUserInfo(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      localStorage.setItem('userInfo', JSON.stringify(updated));
      return updated;
    });
  }, []);

  /**
   * Refresh auth state from localStorage
   */
  const refreshAuth = useCallback(() => {
    loadAuthFromStorage();
  }, [loadAuthFromStorage]);

  const isAuthenticated = useMemo(() => {
    return !!token && !!userInfo;
  }, [token, userInfo]);

  const value = useMemo<AuthContextType>(() => ({
    isAuthenticated,
    token,
    userInfo,
    isLoading,
    login,
    logout,
    updateUserInfo,
    refreshAuth,
  }), [isAuthenticated, token, userInfo, isLoading, login, logout, updateUserInfo, refreshAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 * 
 * @example
 * ```tsx
 * const { isAuthenticated, userInfo, login, logout } = useAuth();
 * 
 * if (!isAuthenticated) {
 *   return <LoginPage />;
 * }
 * 
 * return <Dashboard user={userInfo} onLogout={logout} />;
 * ```
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * HOC to require authentication
 */
export function withAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>
): React.FC<P> {
  const WithAuthComponent: React.FC<P> = (props) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      window.location.href = '/login';
      return null;
    }

    return <WrappedComponent {...props} />;
  };

  WithAuthComponent.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithAuthComponent;
}

export default AuthContext;
